import cx from 'classnames'
import Avatar from 'components/Avatar'
import Icon, { IconName } from 'components/Icon'
import { computeDeadlineDisplay, DeadlineProximity } from 'jobs/deadline'
import { observer } from 'mobx-react'
import OpportunityViewModel from 'opportunities/OpportunityViewModel'
import * as React from 'react'
import { isChildrenEmpty } from 'utils/react-util'
import styles from './OpportunityListItem.sass'
import Opportunity from '../../../../Opportunity'
import { formattedTimeUntil } from '../../../../../utils/date-util'
import OpportunityItem, {
  OpportunityItemAvatarInfo,
  OpportunityItemAvatar,
  OpportunityItemInfo,
  OpportunityItemTitle,
  OpportunityItemSubtitle,
  ForwardIcon,
} from '../../../../components/OpportunityItem'
import { expr } from 'mobx-utils'

interface OpportunityListItemProps {
  opportunity: OpportunityViewModel
  showWorkspaceInfo: boolean
  onSelect: (opportunity: OpportunityViewModel) => unknown
  selected: boolean
}

const deadlineDateFormatter = new Intl.DateTimeFormat('en-US', {
  day: 'numeric',
  month: 'long',
  year: 'numeric',
})

const deadlineTimeFormatter = new Intl.DateTimeFormat('en-US', {
  hour12: true,
  hour: 'numeric',
  minute: 'numeric',
})

export default observer(function OpportunityListItem({
  opportunity: vm,
  showWorkspaceInfo,
  onSelect,
  selected,
}: OpportunityListItemProps) {
  const { opportunity, disabled } = vm
  return (
    <OpportunityItem
      disabled={disabled}
      selected={selected}
      onSelect={() => onSelect(vm)}
      data-test={'opportunity-list-item-' + opportunity.name}
    >
      <OpportunityItemAvatarInfo>
        <OpportunityItemAvatar
          disabled={disabled}
          avatar={
            <Avatar
              subject={
                opportunity.client.canViewInfo ? opportunity.client : undefined
              }
            />
          }
          secondaryAvatar={
            showWorkspaceInfo && (
              <Avatar
                size={16}
                subject={{
                  avatar: opportunity.workspace.branding.logos.icon,
                  avatarFallback: require('assets/logos/logo_worklayer-blue.svg'),
                }}
              />
            )
          }
        />
        <OpportunityItemInfo>
          <OpportunityItemTitle
            disabled={disabled}
            title={opportunity.name}
            secondaryTitle={
              showWorkspaceInfo ? opportunity.workspace.name : undefined
            }
          />
          <OpportunityItemSubtitle>
            {opportunity.scopeItems.map((i) => i.name).join(' • ') ||
              'No description'}
          </OpportunityItemSubtitle>
          <DispatchCountdown opportunity={opportunity} />
        </OpportunityItemInfo>
      </OpportunityItemAvatarInfo>
      <Columns>
        <DeadlineOrSelectionTimeLabel opportunity={opportunity} />
        <ProCutOrSelectionTimeRemaining opportunityVm={vm} />
      </Columns>
      <ForwardIcon disabled={disabled} />
    </OpportunityItem>
  )
})

const DispatchCountdown = observer(function DispatchCountdown({
  opportunity,
}: {
  opportunity: Opportunity
}) {
  if (!opportunity.isDispatchOpportunity || !opportunity.timeUntilSelection) {
    return null
  }
  return (
    <div className={styles.smallScreenSelectionCountdown}>
      <Icon className={styles.timeUntilSelectionIcon} name="clock" auto />
      Time until selection&nbsp;&nbsp;
      <span className={styles.countdown}>
        {formattedTimeUntil(opportunity.timeUntilSelection)}
      </span>
    </div>
  )
})

/**
 * Component for showing the job's deadline.
 */
const DateDeadline = observer(function DateDeadline({
  opportunity,
}: {
  opportunity: Opportunity
}) {
  const deadlineDisplay = expr(() =>
    computeDeadlineDisplay({
      status: 'UNCLAIMED',
      dateDeadline: opportunity.dateDeadline,
    }),
  )

  return opportunity.shouldShowDeadline && opportunity.dateDeadline ? (
    <div className={styles.deadlineOrSelectionTimeLabel}>
      <Column
        description="Due date"
        icon={
          <ColumnIcon
            name="clock"
            variant={deadlineProximityToIconVariant(deadlineDisplay.proximity)}
          />
        }
      >
        <span>{deadlineDateFormatter.format(opportunity.dateDeadline)}</span>
        <br />
        <span>at {deadlineTimeFormatter.format(opportunity.dateDeadline)}</span>
      </Column>
    </div>
  ) : (
    <></>
  )
})

/**
 * A component that displays either the deadline or the time selection label,
 * depending on whether a provider selection is ongoing.
 */
const DeadlineOrSelectionTimeLabel = observer(
  function DeadlineOrSelectionTimeLabel({
    opportunity,
  }: {
    opportunity: Opportunity
  }) {
    return opportunity.isDispatchOpportunity &&
      opportunity.timeUntilSelection ? (
      <div className={styles.deadlineOrSelectionTimeLabel}>
        <div className={styles.timeUntilSelectionCell}>
          <Icon className={styles.timeUntilSelectionIcon} name="clock" auto />
          Time until selection
        </div>
      </div>
    ) : (
      <DateDeadline opportunity={opportunity} />
    )
  },
)

/**
 * Component that shows either the pro cut or the selection time remaining
 * depending on whether a provider is being selected for the job.
 */
const ProCutOrSelectionTimeRemaining = observer(
  function ProCutOrSelectionTimeRemaining({
    opportunityVm,
  }: {
    opportunityVm: OpportunityViewModel
  }) {
    return (
      <div className={styles.proCutOrSelectionTimeRemaining}>
        {opportunityVm.opportunity.isDispatchOpportunity &&
        opportunityVm.opportunity.timeUntilSelection ? (
          <span className={styles.countdown}>
            {formattedTimeUntil(opportunityVm.opportunity.timeUntilSelection)}
          </span>
        ) : (
          opportunityVm.opportunity.shouldShowProviderCutAmount && (
            <Column description="Total" hideOnSmallerScreens>
              {opportunityVm.opportunity.formattedProviderCutAmount}
            </Column>
          )
        )}
      </div>
    )
  },
)

interface ColumnsProps {
  children?: React.ReactNode
}

const Columns = ({ children }: ColumnsProps) => {
  if (isChildrenEmpty(children)) return null
  return <div className={styles.columns}>{children}</div>
}

type IconVariant = 'neutral' | 'info' | 'success' | 'warning' | 'danger'

interface ColumnProps {
  children?: React.ReactNode
  description?: React.ReactNode
  icon?: React.ReactNode
  variant?: IconVariant
  tooltip?: string
  hideOnSmallerScreens?: boolean
}

const Column = ({
  tooltip,
  children,
  icon,
  description,
  hideOnSmallerScreens,
  variant = 'neutral',
}: ColumnProps) => (
  <div
    className={cx(
      styles.column,
      styles[variant],
      hideOnSmallerScreens && styles.hideOnSmallerScreens,
    )}
    title={tooltip}
  >
    <div className={styles.columnTitle}>{children}</div>
    <div className={styles.columnContent}>
      {icon}
      <div className={styles.columnDescription}>{description}</div>
    </div>
  </div>
)

interface ColumnIconProps {
  name: IconName
  variant?: IconVariant
}

const ColumnIcon = ({ name, variant = 'neutral' }: ColumnIconProps) => (
  <span className={cx(styles.columnIcon, styles[variant])}>
    <Icon name={name} auto />
  </span>
)

function deadlineProximityToIconVariant(
  proximity: DeadlineProximity,
): IconVariant {
  switch (proximity) {
    case DeadlineProximity.PrettyFar:
      return 'success'
    case DeadlineProximity.Far:
      return 'warning'
    case DeadlineProximity.Close:
      return 'danger'
    default:
      return 'neutral'
  }
}
